import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { Booking, Container } from "../types/booking";
import {
  AgentContainerProductRow,
  getContainerProductsFromBookings,
} from "./agentContainerProducts";
import { getBookingProducts } from "./agentBookingMetrics";
export type AgentContainerExportFormat = "pdf" | "excel" | "csv";

export interface AgentBookingExportRow {
  Reference: string;
  Company: string;
  Pallets: string | number;
  Cartons: string | number;
  "Gross Weight (kg)": string | number;
  CBM: string | number;
  Delivery: string;
}

export interface AgentProductExportRow {
  EAN: string;
  Name: string;
  "Booking Ref": string;
  Cartons: string | number;
  CBM: string | number;
  "Gross Weight (kg)": string | number;
  Pieces: string | number;
  "HS Code": string;
}

export interface AgentPrintProductRow extends AgentContainerProductRow {
  company_name: string;
  booking_ref: string;
}

export interface AgentContainerExportMeta {
  reference: string;
  transportType: string;
  status: string;
  origin: string;
  destination: string;
  etd: string;
  eta: string;
  generatedOn: string;
  fileBase: string;
}

const resolveInfo = <T,>(value: T | T[] | null | undefined): T | undefined =>
  value == null ? undefined : Array.isArray(value) ? value[0] : value;

const getBookingRef = (booking: Booking): string => {
  const transport = resolveInfo(booking.transport_type_info);
  const abbr = transport?.abbreviation || "";
  return `EFF${booking.id}${abbr}`;
};

const getCompanyName = (booking: Booking): string => {
  const company = resolveInfo(booking.company_info);
  return company?.name || "-";
};

const getBookingTotals = (booking: Booking) => {
  const products = getBookingProducts(booking);
  return {
    cartons: products.reduce((sum, p) => sum + (p.cartons || 0), 0),
    weight: products.reduce((sum, p) => sum + (p.gross_weight || 0), 0),
    cbm: products.reduce((sum, p) => sum + (p.cbm || 0), 0),
  };
};

const formatPort = (
  port: { name?: string; country?: string } | undefined
): string => {
  if (!port?.name) return "-";
  return port.country ? `${port.name} (${port.country})` : port.name;
};

export const getAgentContainerExportMeta = (
  container: Container
): AgentContainerExportMeta => {
  const transport = resolveInfo(container.transport_type_info as never) as
    | { name?: string }
    | undefined;
  const origin = resolveInfo(container.origin_port_info);
  const destination = resolveInfo(container.destination_port_info);
  const reference = container.reference || `Container ${container.id}`;
  const safeRef = reference.replace(/[^\w.-]+/g, "_");

  return {
    reference,
    transportType: transport?.name || "-",
    status: container.status || "-",
    origin: formatPort(origin),
    destination: formatPort(destination),
    etd: container.delayed_etd || container.etd
      ? dayjs(container.delayed_etd || container.etd).format("DD/MM/YYYY")
      : "-",
    eta: container.eta ? dayjs(container.eta).format("DD/MM/YYYY") : "-",
    generatedOn: new Date().toLocaleDateString(),
    fileBase: safeRef,
  };
};

export const enrichProductsForExport = (
  bookings: Booking[]
): AgentPrintProductRow[] =>
  getContainerProductsFromBookings(bookings).map((product) => {
    const booking =
      bookings.find((b) => b.id === product.booking_id) ||
      bookings.find((b) =>
        getBookingProducts(b).some((p) => p.id === product.id)
      );
    return {
      ...product,
      company_name: booking ? getCompanyName(booking) : "-",
      booking_ref: booking ? getBookingRef(booking) : `#${product.booking_id}`,
    };
  });

export const buildAgentBookingsExportRows = (
  bookings: Booking[]
): AgentBookingExportRow[] =>
  bookings.map((booking) => {
    const totals = getBookingTotals(booking);
    return {
      Reference: getBookingRef(booking),
      Company: getCompanyName(booking),
      Pallets: "-",
      Cartons: totals.cartons > 0 ? totals.cartons : "-",
      "Gross Weight (kg)":
        totals.weight > 0 ? Number(totals.weight.toFixed(2)) : "-",
      CBM: totals.cbm > 0 ? Number(totals.cbm.toFixed(3)) : "-",
      Delivery: "-",
    };
  });

export const buildAgentProductsExportRows = (
  bookings: Booking[]
): AgentProductExportRow[] =>
  enrichProductsForExport(bookings).map((product) => ({
    EAN: product.ean || "-",
    Name: product.name || "-",
    "Booking Ref": product.booking_ref,
    Cartons: product.cartons ?? "-",
    CBM:
      product.cbm != null ? Number(Number(product.cbm).toFixed(3)) : "-",
    "Gross Weight (kg)": product.gross_weight ?? "-",
    Pieces: product.pieces ?? "-",
    "HS Code": product.hs_code || "-",
  }));

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadSheetAsExcel = (
  rows: Record<string, unknown>[],
  sheetName: string,
  filename: string
) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, filename);
};

const downloadSheetAsCsv = (
  rows: Record<string, unknown>[],
  filename: string
) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  triggerBlobDownload(blob, filename);
};

export const downloadAgentContainerBookings = async (
  container: Container,
  bookings: Booking[],
  format: AgentContainerExportFormat
): Promise<void> => {
  const meta = getAgentContainerExportMeta(container);
  const rows = buildAgentBookingsExportRows(bookings);

  if (format === "pdf") {
    const { downloadAgentContainerBookingsPdf } = await import(
      "./agentContainerExportPdf"
    );
    await downloadAgentContainerBookingsPdf(meta, rows);
    return;
  }
  if (format === "excel") {
    downloadSheetAsExcel(
      rows,
      "Bookings",
      `${meta.fileBase}-bookings.xlsx`
    );
    return;
  }
  downloadSheetAsCsv(rows, `${meta.fileBase}-bookings.csv`);
};

export const downloadAgentContainerProducts = async (
  container: Container,
  bookings: Booking[],
  format: AgentContainerExportFormat
): Promise<void> => {
  const meta = getAgentContainerExportMeta(container);
  const rows = buildAgentProductsExportRows(bookings);

  if (format === "pdf") {
    const { downloadAgentContainerProductsPdf } = await import(
      "./agentContainerExportPdf"
    );
    await downloadAgentContainerProductsPdf(meta, rows);
    return;
  }
  if (format === "excel") {
    downloadSheetAsExcel(
      rows,
      "Products",
      `${meta.fileBase}-products.xlsx`
    );
    return;
  }
  downloadSheetAsCsv(rows, `${meta.fileBase}-products.csv`);
};
